/**
 * FormateurDebriefingSection — création, listage et détail des débriefings.
 *
 * 11/02/2026 (Phase 3 v2 — Superpouvoirs formateur).
 *
 * UI :
 *   1. Liste des débriefings existants (id, titre, réponses/cibles, statut)
 *   2. Bouton "Nouveau débriefing" ouvre un formulaire de création :
 *      - Cohorte cible + titre + intro
 *      - Liste dynamique de questions (add/remove) : text / number / choice
 *   3. Clic sur un débriefing → panneau détail avec agrégation des réponses.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Plus, Trash2, X, Send, Loader2, Check } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function api(path, options = {}) {
  const resp = await fetch(`${BACKEND}${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${resp.status}`);
  }
  return resp.json();
}

const newQid = () => `q${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export default function FormateurDebriefingSection({ cohorts, onFeedback }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/master/debriefings');
      setItems(data.items || []);
    } catch (err) {
      onFeedback?.('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [onFeedback]);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = async (id) => {
    try {
      const d = await api(`/api/master/debriefings/${id}`);
      setDetail(d);
    } catch (err) {
      onFeedback?.('error', err.message);
    }
  };

  const closeOne = async (id) => {
    if (!window.confirm('Fermer ce débriefing ? Les apprentis ne pourront plus y répondre.')) return;
    try {
      await api(`/api/master/debriefings/${id}`, { method: 'DELETE' });
      onFeedback?.('success', 'Débriefing fermé.');
      reload();
    } catch (err) {
      onFeedback?.('error', err.message);
    }
  };

  return (
    <section className="bg-ink-800/60 border border-ink-600 rounded-lg p-4" data-testid="debriefing-section">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-indigo-300" />
          <h3 className="font-display text-sm font-bold tracking-tight uppercase text-foreground">
            Débriefing collectif ({items.length})
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-indigo-500 text-white hover:bg-indigo-500/80"
          data-testid="debriefing-toggle-create"
        >
          <Plus className="w-3.5 h-3.5" /> {showCreate ? 'Fermer' : 'Nouveau'}
        </button>
      </header>
      <p className="text-xs text-slate-400 mb-3">
        Créez un formulaire pédagogique (texte, chiffres, QCM) pour toute une cohorte. Les apprentis reçoivent une invitation en jeu et vous voyez les réponses agrégées.
      </p>

      {showCreate && (
        <DebriefingCreateForm
          cohorts={cohorts}
          onCreated={() => { setShowCreate(false); reload(); onFeedback?.('success', 'Débriefing créé.'); }}
          onFeedback={onFeedback}
        />
      )}

      {loading ? (
        <div className="text-xs text-slate-500 italic">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 italic" data-testid="debriefing-empty">
          Aucun débriefing pour l&apos;instant.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => (
            <li key={d.id} className="bg-ink-900/40 border border-ink-600 rounded p-2 flex items-center justify-between" data-testid={`debriefing-row-${d.id}`}>
              <button
                onClick={() => openDetail(d.id)}
                className="flex-1 text-left"
                data-testid={`debriefing-open-${d.id}`}
              >
                <div className="text-xs font-bold text-foreground flex items-center gap-2">
                  {d.title}
                  {d.closed_at && <span className="text-[10px] bg-slate-500/20 text-slate-400 px-1.5 rounded">Fermé</span>}
                </div>
                <div className="text-[10px] text-slate-500">
                  {d.response_count} / {d.target_count} réponses · Cohorte {d.cohort_name}
                </div>
              </button>
              {!d.closed_at && (
                <button aria-label="Fermer"
                  onClick={() => closeOne(d.id)}
                  className="text-slate-500 hover:text-red-400 p-1"
                  title="Fermer"
                  data-testid={`debriefing-close-${d.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {detail && <DebriefingDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </section>
  );
}


function DebriefingCreateForm({ cohorts, onCreated, onFeedback }) {
  const [cohortId, setCohortId] = useState('');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [questions, setQuestions] = useState([
    { id: newQid(), text: '', kind: 'text', choices: [] },
  ]);
  const [saving, setSaving] = useState(false);

  const updateQ = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => i === idx ? { ...q, ...patch } : q));
  };
  const removeQ = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));
  const addQ = () => setQuestions((qs) => [...qs, { id: newQid(), text: '', kind: 'text', choices: [] }]);

  const canSubmit = cohortId && title.trim() && questions.every((q) => q.text.trim() && (q.kind !== 'choice' || q.choices.filter(Boolean).length >= 2));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const cleanQuestions = questions.map((q) => ({
        id: q.id,
        text: q.text.trim(),
        kind: q.kind,
        choices: q.kind === 'choice' ? q.choices.filter(Boolean).map((c) => c.trim()) : undefined,
      }));
      await api('/api/master/debriefings', {
        method: 'POST',
        body: JSON.stringify({
          cohort_id: cohortId,
          title: title.trim(),
          intro: intro.trim(),
          questions: cleanQuestions,
        }),
      });
      onCreated();
    } catch (err) {
      onFeedback?.('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-ink-900/50 border border-indigo-500/30 rounded p-3 mb-3 space-y-2" data-testid="debriefing-create-form">
      <select
        value={cohortId}
        onChange={(e) => setCohortId(e.target.value)}
        className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
        data-testid="debriefing-cohort-select"
      >
        <option value="">— Cohorte cible —</option>
        {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.apprentice_keys.length})</option>)}
      </select>
      <input
        type="text"
        placeholder="Titre du débriefing"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={150}
        className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
        data-testid="debriefing-title"
      />
      <textarea
        value={intro}
        onChange={(e) => setIntro(e.target.value)}
        placeholder="Introduction pédagogique (optionnel)"
        maxLength={600}
        className="w-full bg-ink-950 border border-ink-600 rounded p-2 text-xs text-slate-400 h-14 resize-none focus:outline-none focus:border-accent"
        data-testid="debriefing-intro"
      />
      {/* Questions */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Questions ({questions.length})</div>
        {questions.map((q, idx) => (
          <div key={q.id} className="bg-ink-950/40 border border-ink-700 rounded p-2 space-y-1" data-testid={`debriefing-question-${idx}`}>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={`Question ${idx + 1}`}
                value={q.text}
                onChange={(e) => updateQ(idx, { text: e.target.value })}
                className="flex-1 bg-ink-950 border border-ink-600 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-accent"
                data-testid={`debriefing-question-text-${idx}`}
              />
              <select
                value={q.kind}
                onChange={(e) => updateQ(idx, { kind: e.target.value, choices: e.target.value === 'choice' ? ['', ''] : [] })}
                className="bg-ink-950 border border-ink-600 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none"
                data-testid={`debriefing-question-kind-${idx}`}
              >
                <option value="text">Texte</option>
                <option value="number">Nombre</option>
                <option value="choice">QCM</option>
              </select>
              {questions.length > 1 && (
                <button aria-label="Fermer" onClick={() => removeQ(idx)} className="text-slate-500 hover:text-red-400" data-testid={`debriefing-question-remove-${idx}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {q.kind === 'choice' && (
              <div className="pl-2 space-y-1">
                {q.choices.map((c, cIdx) => (
                  <input
                    key={cIdx}
                    type="text"
                    placeholder={`Choix ${cIdx + 1}`}
                    value={c}
                    onChange={(e) => {
                      const next = [...q.choices];
                      next[cIdx] = e.target.value;
                      updateQ(idx, { choices: next });
                    }}
                    className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1 text-[11px] text-slate-300 focus:outline-none focus:border-accent"
                  />
                ))}
                <button
                  onClick={() => updateQ(idx, { choices: [...q.choices, ''] })}
                  className="text-[10px] text-slate-400 hover:text-accent"
                >
                  + Ajouter un choix
                </button>
              </div>
            )}
          </div>
        ))}
        <button
          onClick={addQ}
          className="text-xs text-slate-400 hover:text-accent inline-flex items-center gap-1"
          data-testid="debriefing-add-question"
        >
          <Plus className="w-3 h-3" /> Ajouter une question
        </button>
      </div>
      <button
        onClick={submit}
        disabled={!canSubmit || saving}
        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded bg-indigo-500 text-white hover:bg-indigo-500/80 disabled:opacity-40"
        data-testid="debriefing-submit"
      >
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        <Send className="w-3 h-3" />
        Créer & envoyer
      </button>
    </div>
  );
}


function DebriefingDetailModal({ detail, onClose }) {
  const questions = detail.questions || [];
  const agg = detail.aggregate_by_question || {};

  return (
    <div className="fixed inset-0 z-[9990] bg-ink-950/80 flex items-center justify-center p-4" data-testid="debriefing-detail-modal">
      <div className="bg-ink-800 border border-ink-600 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <header className="px-4 py-3 border-b border-ink-600 flex items-center justify-between sticky top-0 bg-ink-800 z-10">
          <div>
            <h3 className="font-display text-sm font-bold text-foreground">{detail.title}</h3>
            <p className="text-[11px] text-slate-500">
              {detail.responses?.length || 0} / {detail.target_apprentice_keys?.length || 0} réponses reçues
            </p>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="text-slate-400 hover:text-red-400" data-testid="debriefing-detail-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-4 space-y-4">
          {detail.intro && <p className="text-xs text-slate-400 italic">{detail.intro}</p>}
          {questions.map((q) => {
            const responses = agg[q.id] || [];
            return (
              <div key={q.id} className="bg-ink-900/50 border border-ink-600 rounded p-3">
                <div className="text-xs font-bold text-foreground mb-1">{q.text}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{q.kind}</div>
                {responses.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic">Aucune réponse.</div>
                ) : q.kind === 'choice' ? (
                  <ChoiceAggregate responses={responses} choices={q.choices || []} />
                ) : (
                  <ul className="space-y-1">
                    {responses.map((r, i) => (
                      <li key={i} className="text-[11px] border-l-2 border-indigo-400 pl-2">
                        <span className="text-slate-400 font-medium">{r.apprentice_holder} :</span>
                        <span className="text-slate-200 ml-1">{String(r.value)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ChoiceAggregate({ responses, choices }) {
  const counts = {};
  for (const c of choices) counts[c] = 0;
  for (const r of responses) {
    if (counts[r.value] != null) counts[r.value] += 1;
  }
  const total = responses.length || 1;
  return (
    <div className="space-y-1">
      {choices.map((c) => {
        const n = counts[c] || 0;
        const pct = Math.round((n / total) * 100);
        return (
          <div key={c} className="flex items-center gap-2 text-[11px]">
            <span className="w-24 truncate text-slate-300">{c}</span>
            <div className="flex-1 bg-ink-950 rounded h-1.5 relative overflow-hidden">
              <div className="absolute left-0 top-0 h-full bg-indigo-400" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-slate-400 font-mono">{n} · {pct}%</span>
          </div>
        );
      })}
    </div>
  );
}
